'use strict';

(function initializeDiceTrayRecentAdapter(root) {
    const app = root.dndNextLevel;
    const TYPE = 'dice';
    const RESTORE_KEY = 'dnd-next-level:dicetray:restore:v1';
    const SIDES = new Set([4, 6, 8, 10, 12, 20, 100]);
    const hasText = (value, max = 2_000) => typeof value === 'string'
        && value.trim().length > 0
        && value.length <= max;
    const isInteger = value => Number.isInteger(value);
    const sum = values => values.reduce((total, value) => total + value, 0);
    const clone = value => JSON.parse(JSON.stringify(value));

    const isValidGroup = (group, index) => Boolean(
        group && typeof group === 'object'
        && group.groupId === index
        && isInteger(group.quantity) && group.quantity >= 1 && group.quantity <= 20
        && isInteger(group.sides) && SIDES.has(group.sides)
        && hasText(group.normalizedNotation, 100)
        && Array.isArray(group.rolls) && group.rolls.length === group.quantity
        && group.rolls.every(roll => isInteger(roll) && roll >= 1 && roll <= group.sides)
        && isInteger(group.subtotal) && group.subtotal === sum(group.rolls)
    );

    const isValidRoll = roll => {
        if (!roll || typeof roll !== 'object'
            || !hasText(roll.requestedNotation, 200)
            || !hasText(roll.normalizedNotation, 200)
            || !isInteger(roll.modifier) || Math.abs(roll.modifier) > 100
            || !Array.isArray(roll.groups) || roll.groups.length < 1 || roll.groups.length > 20
            || !roll.groups.every(isValidGroup)
            || !Array.isArray(roll.rolls)
            || !roll.rolls.every(isInteger)
            || !isInteger(roll.subtotal)
            || !isInteger(roll.total)
            || !hasText(roll.summary)) return false;

        const groupedRolls = roll.groups.flatMap(group => group.rolls);
        return groupedRolls.length <= 20
            && groupedRolls.length === roll.rolls.length
            && groupedRolls.every((value, index) => value === roll.rolls[index])
            && roll.subtotal === sum(groupedRolls)
            && roll.total === roll.subtotal + roll.modifier;
    };

    const adapter = {
        type: TYPE,
        route: '/dicetray',
        capture(source) {
            const roll = source?.roll ?? source;
            return isValidRoll(roll) ? {version: 1, roll: clone(roll)} : null;
        },
        validate(payload) {
            return payload?.version === 1 && isValidRoll(payload.roll);
        },
        preview(payload) {
            if (!adapter.validate(payload)) throw new Error('Invalid Dice recent creation');
            const roll = payload.roll;
            return {
                title: `${roll.normalizedNotation} = ${roll.total}`,
                description: `Rolls: ${roll.rolls.join(', ')} · Modifier: ${roll.modifier >= 0 ? '+' : ''}${roll.modifier}`,
            };
        },
        restore(payload) {
            if (!adapter.validate(payload)) throw new Error('Invalid Dice recent creation');
            root.sessionStorage.setItem(RESTORE_KEY, JSON.stringify(payload));
            root.location.assign(adapter.route);
            return clone(payload.roll);
        },
    };

    const slug = value => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const entryId = roll => [slug(roll.normalizedNotation), ...roll.rolls, roll.total].join('-');

    const save = (roll, storage = root.localStorage, now = Date.now()) => {
        const payload = adapter.capture(roll);
        if (!payload || !app.recentCreationStore) return null;
        const store = app.recentCreationStore.create(storage);
        const id = entryId(payload.roll);
        const previous = store.read().find(entry => entry.type === TYPE && entry.id === id);
        const entry = {
            id,
            type: TYPE,
            createdAt: previous?.createdAt ?? now,
            updatedAt: now,
            pinned: true,
            payload,
        };
        store.save(entry);
        app.recentCreations?.instance?.render();
        return entry;
    };

    const consumeRestore = (storage = root.sessionStorage) => {
        const raw = storage.getItem(RESTORE_KEY);
        storage.removeItem(RESTORE_KEY);
        if (!raw) return null;
        try {
            const payload = JSON.parse(raw);
            return adapter.validate(payload) ? clone(payload.roll) : null;
        } catch (_error) {
            return null;
        }
    };

    const api = {adapter, save, consumeRestore, RESTORE_KEY, isValidRoll, entryId};
    app.diceTrayRecentAdapter = api;
    app.dicetray = app.dicetray || {};
    app.dicetray.recentAdapter = api;
    app.recentCreationAdapters?.registry?.register(adapter);
})(window);
